import { useEffect, useState, type JSX } from 'react';
import { DocAccessType, IFollowUpDocuments } from '../../../../../types';
import NoDocuments from '../../../../atoms/no-doc-placeholder';
import {
  hasPerm,
  isAgentOrAgencyRole,
  isEnrolyStaff,
  isPartnerRole,
  isUniStaffRole,
  P,
} from '../../../../../utilities/roles/permissions';
import { UserRole } from '../../../../../store/auth/types';
import { FileUploadModal } from '../../../modals/fileUpload/index';
import { IModal } from '../../../../../store/ui/types';
import CustomRequestDocumentModal from '../../../modals/customRequestDoc/container';
import FileDeleteModal from '../../../modals/fileDelete/container';
import FileRejectModal from '../../../modals/fileReject/container';
import VisaVignette from '../../../../molecules/followUpDocumentSection/beforeTravel/visaVignette';
import TravelTicket from '../../../../molecules/followUpDocumentSection/beforeTravel/travelTicket';
import VisaApplicationPdfCopy from '../../../../molecules/followUpDocumentSection/beforeTravel/visaApplicationPdfCopy';
import CustomRequestDocument from '../../../../molecules/followUpDocumentSection/customRequest';
import {
  ImmigrationStatusPdfCopy,
  ImmigrationStatusShareCodeModal,
  StudentRefusalLetterModal,
  uiTypes,
} from 'shared-components';
import { ImmigrationStatus } from '../../../../molecules/followUpDocumentSection/beforeTravel/ImmigrationStatusShareCode';
import StudentRefusalLetter from '../../../../molecules/followUpDocumentSection/beforeTravel/studentRefusalLetter';
import {
  IFollowUpArea,
  ImmigrationStatusShareCodeDoc,
  ServiceProviderType,
  StudentRefusalLetterDoc,
} from 'api-paths-cas';
import { Box } from '@chakra-ui/react';
import FileRescindRejectionModal from '../../../modals/fileRescindRejection';
import { useDispatch, useSelector } from 'react-redux';
import { getStudentFollowUpDocs } from '../../../../../store/student/actions';
import { DocumentRequestButtons } from '../../../../DocumentRequestButtons';
import { SectionGroup } from '../../../../Finger/SectionGroup';
import { DocumentViewer } from 'shared-components';
import { ApplicationState } from '@/store';

export interface IDispatchProps {
  openFollowUpModal: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle?: string,
    data?: Record<string, unknown>,
  ) => void;

  onApproveDoc: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;

  openUploadModal: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string,
    modalTitle: string,
    fileNames: string[],
    metadata?: unknown,
  ) => void;
  closeModal: () => void;
  saveShareCode: (docKey: string, data: Record<string, unknown>) => void;
}

export interface IProps {
  hasRequiredBeforeTravelDocs: boolean;
  hasStaffOnlyBeforeTravelDocs: boolean;
  hasAgentOnlyBeforeTravelDocs: boolean;
  openModal: IModal | null;
  documents: IFollowUpDocuments;
  dob?: string;
  userRole: UserRole;
  serviceProviderType?: ServiceProviderType;
  isBeforeArrivalAreaHidden: boolean;
  beforeDocumentsRequired: number;
}

type Props = IProps & IDispatchProps;

function BeforeTravelDocumentsDashboard(props: Props): JSX.Element {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getStudentFollowUpDocs());
  }, []);

  const currentUserRole = useSelector(({ auth }: ApplicationState) => auth.role);

  const isAgent = isAgentOrAgencyRole(currentUserRole);
  const isStaff = isUniStaffRole(currentUserRole);
  const isEnrolyOrPartner = isEnrolyStaff(currentUserRole) || isPartnerRole(currentUserRole);

  const canResume = hasPerm(props.userRole, P.CHECKLIST_EDIT);
  const canUpload = hasPerm(props.userRole, P.FOLLOW_UP_DOCUMENTS_UPLOAD);
  const canOpen = hasPerm(props.userRole, P.ALL_DOCUMENTS_PREVIEW, props.serviceProviderType);
  const canApprove = hasPerm(props.userRole, P.ALL_DOCUMENTS_APPROVE);
  const canReject = hasPerm(props.userRole, P.ALL_DOCUMENTS_REJECT);
  const canUploadStaffOnly = hasPerm(props.userRole, P.UPLOAD_STAFF_ONLY_DOCS) && (isStaff || isEnrolyOrPartner);
  const canUploadAgentOnly =
    hasPerm(props.userRole, P.UPLOAD_AGENT_ONLY_DOCS) && (isAgent || isStaff || isEnrolyOrPartner); // Staff and enroly has access of agent docs as well
  const canRequest = hasPerm(props.userRole, P.CUSTOM_DOCUMENTS_REQUEST);
  const canDelete = hasPerm(props.userRole, P.FOLLOW_UP_DOCUMENTS_DELETE);

  const hasStaffOnlyDocs = props.hasStaffOnlyBeforeTravelDocs && canUploadStaffOnly;
  const hasAgentOnlyDocs = props.hasAgentOnlyBeforeTravelDocs && canUploadAgentOnly;

  const [isDeleting, setIsDeleting] = useState(false);

  const [previewOpen, toggleOpen] = useState(false);
  const [imageArray, setImageArray] = useState<string[] | undefined>();

  const { openModal, closeModal } = props;
  if (openModal?.isUpload) {
    return <FileUploadModal {...openModal} />;
  }
  if (openModal) {
    if (openModal.sponsorType === 'customRequestModal') {
      return <CustomRequestDocumentModal followUpArea={IFollowUpArea.BEFORE_TRAVEL} />;
    }
    if (openModal?.docType?.startsWith('delete-')) {
      return <FileDeleteModal onDelete={() => setIsDeleting(false)} />;
    }
    if (props.openModal?.docType?.startsWith('reject-')) {
      return <FileRejectModal />;
    }
    if (openModal.docType?.startsWith('rescind-reject-')) {
      return <FileRescindRejectionModal />;
    }
  }

  switch (openModal?.sponsorType) {
    case 'beforeTravel':
      switch (openModal?.docType) {
        case 'studentRefusalLetter': {
          const docKey = openModal.docKey ?? '';
          const doc = props.documents.beforeTravel?.studentRefusalLetter?.[docKey] ?? ({} as StudentRefusalLetterDoc);
          return (
            <StudentRefusalLetterModal
              docKey={docKey}
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              refusalDate={doc.refusalDate}
              applicationDate={doc.applicationDate}
              attendedInterview={doc.attendedInterview}
              refusalGrounds={doc.refusalGrounds}
              refusalGroundsInfo={doc.refusalGroundsInfo}
              appliedForAdminReview={doc.appliedForAdminReview}
              adminReviewDate={doc.adminReviewDate}
              fileNames={doc.fileNames}
              onContinue={() => {
                props.openFollowUpModal('beforeTravel', null, 'studentRefusalLetter', docKey, 'Student Visa Refusal');
              }}
              onUpload={(
                sponsorType: string | null,
                _sponsorKey: string | null,
                docType: string | null,
                _docKey: string | null,
                modalTitle: string,
                fileNames: string[],
                metaData: uiTypes.IStudentRefusalLetterMetaData,
              ) => {
                props.openUploadModal(sponsorType, null, docType, docKey, modalTitle, fileNames ?? [], metaData);
              }}
              onClose={props.closeModal}
            />
          );
        }
        case 'immigrationStatusShareCode': {
          const docKey = openModal.docKey ?? '';
          return (
            <ImmigrationStatusShareCodeModal
              closeModal={closeModal}
              docKey={docKey}
              shareCode={openModal.shareCode ?? ({} as ImmigrationStatusShareCodeDoc)}
              title={openModal.modalTitle ?? ''}
              onSave={(saveDocKey: string, data: Record<string, unknown>) => {
                props.saveShareCode(saveDocKey, data);
              }}
            />
          );
        }
        case 'immigrationStatusPdfCopy': {
          const docKey = openModal.docKey ?? '';
          const doc =
            props.documents.beforeTravel?.immigrationStatusPdfCopy?.[docKey] ?? ({} as ImmigrationStatusShareCodeDoc);
          return (
            <ImmigrationStatusPdfCopy
              docKey={docKey}
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              onContinue={() => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'immigrationStatusPdfCopy',
                  docKey,
                  'Immigration Status: PDF',
                );
              }}
              onUpload={(
                sponsorType: string | null,
                _sponsorKey: string | null,
                docType: string | null,
                _docKey: string | null,
                modalTitle: string,
                fileNames: string[],
              ) => {
                props.openUploadModal(sponsorType, null, docType, docKey, modalTitle, fileNames ?? []);
              }}
              onClose={props.closeModal}
            />
          );
        }
      }
  }

  const renderBeforeTravelDocs = (userAccess?: DocAccessType): JSX.Element => {
    return (
      <>
        {!props.isBeforeArrivalAreaHidden && (
          <>
            <StudentRefusalLetter
              userAccess={userAccess}
              documents={props.documents.beforeTravel.studentRefusalLetter}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'beforeTravel',
                  null,
                  'studentRefusalLetter',
                  docKey,
                  props.documents.beforeTravel.studentRefusalLetter[docKey].status,
                  props.documents.beforeTravel.studentRefusalLetter[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'delete-studentRefusalLetter',
                  docKey,
                  'Student Visa Refusal',
                );
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.beforeTravel.studentRefusalLetter[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'reject-studentRefusalLetter',
                  docKey,
                  'Student Visa Refusal',
                );
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'rescind-reject-studentRefusalLetter',
                  docKey,
                  'Student Visa Refusal',
                );
              }}
              onUploadClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'studentRefusalLetter', docKey, 'Student Visa Refusal');
              }}
              userRole={props.userRole}
            />

            <VisaVignette
              userAccess={userAccess}
              documents={props.documents.beforeTravel.visaVignette}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'beforeTravel',
                  null,
                  'visaVignette',
                  docKey,
                  props.documents.beforeTravel.visaVignette[docKey].status,
                  props.documents.beforeTravel.visaVignette[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'delete-visaVignette', docKey, 'Visa Vignette');
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.beforeTravel.visaVignette[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'reject-visaVignette', docKey, 'Visa Vignette');
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'rescind-reject-visaVignette', docKey, 'Visa Vignette');
              }}
              onUploadClick={(docKey: string) => {
                props.openUploadModal(
                  'beforeTravel',
                  null,
                  'visaVignette',
                  docKey,
                  'Before Travel',
                  props.documents.beforeTravel.visaVignette[docKey].fileNames,
                );
              }}
              userRole={props.userRole}
            />

            <VisaApplicationPdfCopy
              userAccess={userAccess}
              documents={props.documents.beforeTravel.visaApplicationPdfCopy}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'beforeTravel',
                  null,
                  'visaApplicationPdfCopy',
                  docKey,
                  props.documents.beforeTravel.visaApplicationPdfCopy[docKey].status,
                  props.documents.beforeTravel.visaApplicationPdfCopy[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'delete-visaApplicationPdfCopy',
                  docKey,
                  `Visa Application PDF Copy ${props.documents.beforeTravel.visaApplicationPdfCopy[docKey].date}`,
                );
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.beforeTravel.visaApplicationPdfCopy[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'reject-visaApplicationPdfCopy',
                  docKey,
                  `Visa Application PDF Copy ${props.documents.beforeTravel.visaApplicationPdfCopy[docKey].date}`,
                );
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal(
                  'beforeTravel',
                  null,
                  'rescind-reject-visaApplicationPdfCopy',
                  docKey,
                  `Visa Application PDF Copy ${props.documents.beforeTravel.visaApplicationPdfCopy[docKey].date}`,
                );
              }}
              onUploadClick={(docKey: string) => {
                props.openUploadModal(
                  'beforeTravel',
                  null,
                  'visaApplicationPdfCopy',
                  docKey,
                  `Visa Application PDF Copy ${props.documents.beforeTravel.visaApplicationPdfCopy[docKey].date}`,
                  props.documents.beforeTravel.visaApplicationPdfCopy[docKey].fileNames,
                );
              }}
              userRole={props.userRole}
            />

            <TravelTicket
              userAccess={userAccess}
              documents={props.documents.beforeTravel.travelTicket}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onAcknowledgeClick={() => {}}
              onApproveClick={(docKey: string) => {
                props.onApproveDoc(
                  'beforeTravel',
                  null,
                  'travelTicket',
                  docKey,
                  props.documents.beforeTravel.travelTicket[docKey].status,
                  props.documents.beforeTravel.travelTicket[docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'delete-travelTicket', docKey, 'Travel Ticket');
              }}
              onOpenClick={(docKey: string) => {
                setImageArray(props.documents.beforeTravel.travelTicket[docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'reject-travelTicket', docKey, 'Travel Ticket');
              }}
              onRescindRejectClick={(docKey: string) => {
                props.openFollowUpModal('beforeTravel', null, 'rescind-reject-travelTicket', docKey, 'Travel Ticket');
              }}
              onUploadClick={(docKey: string) => {
                props.openUploadModal(
                  'beforeTravel',
                  null,
                  'travelTicket',
                  docKey,
                  'Travel Ticket',
                  props.documents.beforeTravel.travelTicket[docKey].fileNames,
                );
              }}
              userRole={props.userRole}
            />

            <ImmigrationStatus
              userAccess={userAccess}
              shareCodeDocuments={props.documents.beforeTravel.immigrationStatusShareCode || {}}
              pdfCopyDocuments={props.documents.beforeTravel.immigrationStatusPdfCopy || {}}
              dob={props.dob}
              canResume={canResume}
              canUpload={canUpload}
              canOpen={canOpen}
              canApprove={canApprove}
              canReject={canReject}
              isDeleting={isDeleting}
              onApproveClick={(docKey: string, docType: string) => {
                props.onApproveDoc(
                  'beforeTravel',
                  null,
                  docType,
                  docKey,
                  props.documents.beforeTravel[docType][docKey].status,
                  props.documents.beforeTravel[docType][docKey].prevStatus,
                );
              }}
              onDeleteClick={(docKey: string, docType: string, modalTitle: string) => {
                props.openFollowUpModal('beforeTravel', null, `delete-${docType}`, docKey, modalTitle);
              }}
              onOpenClick={(docKey: string, docType: string) => {
                setImageArray(props.documents.beforeTravel[docType][docKey].signedUrls);
                toggleOpen(true);
              }}
              onRejectClick={(docKey: string, docType: string, modalTitle: string) => {
                props.openFollowUpModal('beforeTravel', null, `reject-${docType}`, docKey, modalTitle);
              }}
              onUploadClick={(docKey: string, docType: string, modalTitle: string) => {
                props.openFollowUpModal('beforeTravel', null, docType, docKey, modalTitle, {
                  shareCode: props.documents.beforeTravel.immigrationStatusShareCode[docKey],
                });
              }}
              userRole={props.userRole}
            />
          </>
        )}

        <CustomRequestDocument
          userAccess={userAccess}
          documents={props.documents.beforeTravel.customRequest}
          canUpload={canUpload}
          canOpen={canOpen}
          canApprove={canApprove}
          canReject={canReject}
          isDeleting={isDeleting}
          onOpenClick={(customReqKey: string, docKey: string) => {
            setImageArray(props.documents.beforeTravel.customRequest[customReqKey]?.[docKey].signedUrls ?? []);
            toggleOpen(true);
          }}
          onUploadClick={(customReqKey: string, docKey: string) => {
            props.openUploadModal(
              'beforeTravel',
              customReqKey,
              'customRequest',
              docKey,
              'Custom Request',
              props.documents.beforeTravel.customRequest[customReqKey]?.[docKey].fileNames,
            );
          }}
          onApproveClick={(customReqKey: string, docKey: string) =>
            props.onApproveDoc(
              'beforeTravel',
              customReqKey,
              'customRequest',
              docKey,
              props.documents.beforeTravel.customRequest[customReqKey]?.[docKey].status,
              props.documents.beforeTravel.customRequest[customReqKey]?.[docKey].prevStatus,
            )
          }
          onRejectClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal('beforeTravel', customReqKey, 'reject-customRequest', docKey, 'Custom Document');
          }}
          onRescindRejectClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal(
              'beforeTravel',
              customReqKey,
              'rescind-reject-customRequest',
              docKey,
              'Custom Document',
            );
          }}
          onDeleteClick={(customReqKey: string, docKey: string) => {
            props.openFollowUpModal('beforeTravel', customReqKey, 'delete-customRequest', docKey, 'Custom Document');
          }}
          userRole={props.userRole}
        />
      </>
    );
  };

  const hasCustomBeforeTravelDocs = Object.keys(props.documents.beforeTravel.customRequest ?? {}).length > 0;

  return (
    <Box>
      {props.hasRequiredBeforeTravelDocs || hasStaffOnlyDocs || hasAgentOnlyDocs || hasCustomBeforeTravelDocs ? (
        <>
          {(props.hasRequiredBeforeTravelDocs || hasCustomBeforeTravelDocs) && (
            <SectionGroup
              heading="Before Travel to the UK"
              description="These documents are requested from the student prior to travel"
              data-selector={'before-arrival-area'}
            >
              {renderBeforeTravelDocs()}
            </SectionGroup>
          )}

          {hasStaffOnlyDocs && (
            <SectionGroup
              heading="The below documents can only be uploaded by a staff member"
              description="The student has not been asked to provide these documents"
              data-selector={'staff-only-docs-section'}
            >
              {props.hasStaffOnlyBeforeTravelDocs ? renderBeforeTravelDocs(DocAccessType.STAFF_ONLY) : null}
            </SectionGroup>
          )}

          {hasAgentOnlyDocs && (
            <SectionGroup
              heading="The below documents can only be uploaded by an agent"
              description="The student has not been asked to provide these documents"
              data-selector={'agent-only-docs-section'}
            >
              {props.hasAgentOnlyBeforeTravelDocs ? renderBeforeTravelDocs(DocAccessType.AGENT_ONLY) : null}
            </SectionGroup>
          )}
        </>
      ) : (
        <NoDocuments />
      )}

      <DocumentRequestButtons
        canRequest={canRequest}
        canDelete={canDelete}
        isDeleting={isDeleting}
        onRequestClick={() =>
          props.openFollowUpModal('customRequestModal', null, null, null, 'Custom Document Request')
        }
        onDeleteClick={() => setIsDeleting(!isDeleting)}
      />

      {previewOpen && imageArray && imageArray.length > 0 && (
        <DocumentViewer documents={imageArray} onClose={() => toggleOpen(false)} />
      )}
    </Box>
  );
}

export default BeforeTravelDocumentsDashboard;
